import { Modal, Typography, Box } from '@mui/material'
import { useRef, useState } from 'react';
import { Notification } from '../../common/Notification/Notification';
import { FileUpload } from '../inputComponents/FileUpload/FileUpload.component';
import { defaultNotifState } from '../../constants/currency-constants';
import { countCsvFilesLines } from '../inputComponents/FileUpload/fileUploadHelpers';
import { WinningMomentsExceedWarningDialog } from '../winningMomentsComponent/WinningMomentsExceedWarningDialog';
import './Modal.css';
import '../inputComponents/FileUpload/FileUpload.css';

interface UploadFileProps {
    modalOpenState: boolean,
    handleClose(modalState:boolean):void,
    configId: string;
    availableVoucherCount: number | null;
    sessionUploadedWinningMomentsCount?: number;
    onWinningMomentsUploaded?: (uploadedWinningMomentsCount: number) => void;
}

function UploadWinningMomentsModal({
    modalOpenState,
    handleClose,
    configId,
    availableVoucherCount,
    sessionUploadedWinningMomentsCount = 0,
    onWinningMomentsUploaded,
}: UploadFileProps) {
    const [notificationState, setNotificationState] = useState(defaultNotifState);
    const [showExceedWarning, setShowExceedWarning] = useState(false);
    const pendingUploadResolver = useRef<((confirmed: boolean) => void) | null>(null);
    const pendingUploadWinningMomentsCount = useRef(0);

    const resolvePendingUpload = (confirmed: boolean) => {
        pendingUploadResolver.current?.(confirmed);
        pendingUploadResolver.current = null;
        if (!confirmed) {
            pendingUploadWinningMomentsCount.current = 0;
        }
        setShowExceedWarning(false);
    };

    const confirmWinningMomentsUpload = async (files: File[]) => {
        if (availableVoucherCount === null) return true;

        const selectedFilesWinningMomentsCount = await countCsvFilesLines(files);
        pendingUploadWinningMomentsCount.current = selectedFilesWinningMomentsCount;

        if (sessionUploadedWinningMomentsCount + selectedFilesWinningMomentsCount <= availableVoucherCount) {
            return true;
        }

        setShowExceedWarning(true);

        return new Promise<boolean>((resolve) => {
            pendingUploadResolver.current = resolve;
        });
    };

    return (
        <Box>
            <Notification notificationState={notificationState} setNotificationState={setNotificationState}/>
            <WinningMomentsExceedWarningDialog
                isOpen={showExceedWarning}
                onCancel={() => resolvePendingUpload(false)}
                onContinue={() => resolvePendingUpload(true)}
            />
            <Modal
            open={modalOpenState}
            onClose={() => { handleClose(false); }}
            data-testid="winningMomentsModal"
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            >
                <Box className='modal_main'>
                    <Box className='body'>
                        <Typography id="modal-modal-title" variant="h6">
                            Upload Winning Moments
                        </Typography>
                    </Box>
                    <FileUpload
                        beforeUpload={confirmWinningMomentsUpload}
                        placeholder='CSV Files with moments'
                        filePath={`${configId}/winningMomentCSVs`}
                        maxFiles={5}
                        uploadCallback={(files: any) => {
                            onWinningMomentsUploaded?.(pendingUploadWinningMomentsCount.current);
                            pendingUploadWinningMomentsCount.current = 0;
                            setNotificationState({
                                open: true,
                                title: `Moments were added successfully`,
                                content:`Moments file${files.length > 1 ? 's' : ''} uploaded: ${files.join(', ')}`,
                                level: "success"
                            });
                        }}
                        deleteCallback={(file: string) => {
                            setNotificationState({
                                open: true,
                                title: `Moments file was removed successfully`,
                                content: `Removed moments file: ${file}`,
                                level: "success"
                            });
                        }}
                        customAllowedFileTypes={['text/csv']}
                        setNotificationState={setNotificationState}
                    />
                    <Typography variant='body1' style={{fontStyle:'italic'}}>You can select multiple files</Typography>
                </Box>
            </Modal>
      </Box>
    )
}

export { UploadWinningMomentsModal }
